import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function useDeleteOne(urlFn, keys) {
  const qc = useQueryClient();
  return async (id, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.delete(urlFn(id));
      toast.success("Deleted");
      (keys || []).forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
}

export function useBulkDelete(urlFn, keys) {
  const qc = useQueryClient();
  return async (items, label = "records") => {
    if (!items?.length) return;
    if (!window.confirm(`Delete all ${items.length} ${label}? This permanently removes the demo data and cannot be undone.`)) return;
    try {
      toast.loading(`Clearing ${label}…`, { id: "bulk" });
      for (const it of items) await api.delete(urlFn(it));
      toast.success(`Cleared ${label}`, { id: "bulk" });
      (keys || []).forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed", { id: "bulk" });
    }
  };
}

export function useList(key, url, params) {
  return useQuery({
    queryKey: [key, params || null],
    queryFn: async () => (await api.get(url, { params })).data,
  });
}

export function useCreate(url, invalidate) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post(url, body)).data,
    onSuccess: () => invalidate && invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}

export function useUpdate(urlFn, invalidate) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => (await api.put(urlFn(id), body)).data,
    onSuccess: () => invalidate && invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}

export function useRemove(urlFn, invalidate) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(urlFn(id))).data,
    onSuccess: () => invalidate && invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
}
